import { useCallback, useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import {
  MOBILE_INVOICE_PREVIEW_EVENT,
  type MobileInvoicePreviewDetail,
  useTranslation,
} from '@spcc/core'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@spcc/uikit'

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

function normalizeShareUri(uri: string): string {
  if (uri.startsWith('file://') || uri.startsWith('content://') || uri.startsWith('http')) {
    return uri
  }
  if (uri.startsWith('/')) return `file://${uri}`
  return uri
}

function isFileShareUri(uri: string): boolean {
  return uri.startsWith('file://')
}

function getNativeShareOptions(
  fileUri: string,
  fileName: string
): { title: string; files?: string[]; url?: string } {
  const normalized = normalizeShareUri(fileUri)
  const platform = typeof Capacitor?.getPlatform === 'function' ? Capacitor.getPlatform() : 'web'

  if (platform === 'android' && isFileShareUri(normalized)) {
    return { title: fileName, files: [normalized] }
  }

  return { title: fileName, url: normalized }
}

function isNativePlatform(): boolean {
  return typeof Capacitor?.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false
}

/**
 * Listens for {@link MOBILE_INVOICE_PREVIEW_EVENT} from finance {@link printInvoice} on native
 * and shows HTML preview + Share of the PDF file. Mount once under `I18nProvider`.
 */
export function MobileInvoicePreviewDrawer() {
  const { t } = useTranslation()
  const [detail, setDetail] = useState<MobileInvoicePreviewDetail | null>(null)
  const [sharing, setSharing] = useState(false)

  useEffect(() => {
    const handlePreview = (event: Event) => {
      const customEvent = event as CustomEvent<MobileInvoicePreviewDetail>
      setDetail(customEvent.detail)
    }

    window.addEventListener(MOBILE_INVOICE_PREVIEW_EVENT, handlePreview as EventListener)
    return () => {
      window.removeEventListener(MOBILE_INVOICE_PREVIEW_EVENT, handlePreview as EventListener)
    }
  }, [])

  const closeDrawer = useCallback(() => {
    setDetail(null)
  }, [])

  const handleShare = useCallback(async () => {
    if (!detail) return
    setSharing(true)

    try {
      if (isNativePlatform()) {
        await Share.share(getNativeShareOptions(detail.fileUri, detail.fileName))
      } else if (typeof navigator.share === 'function') {
        await navigator.share({ title: detail.fileName, text: detail.fileName })
      } else {
        throw new Error('Share not supported')
      }
    } catch (error) {
      console.error('[MobileInvoicePreviewDrawer] Share failed', error)
      toast({
        title: t('common.error'),
        description: t('common.shareFileFailed'),
        variant: 'destructive',
      })
    } finally {
      setSharing(false)
    }
  }, [detail, t])

  return (
    <Dialog open={!!detail} onOpenChange={(open) => { if (!open) closeDrawer() }}>
      <DialogContent className="w-[95vw] max-w-4xl h-[85vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-4 sm:px-6 py-4 border-b bg-gradient-to-r from-slate-900 to-slate-700 text-white shrink-0">
          <DialogTitle>{t('finance.invoicePreviewTitle', 'Invoice preview')}</DialogTitle>
          <DialogDescription className="text-slate-200">
            {t(
              'finance.invoicePreviewSubtitle',
              'Review the invoice below. The shared file is a PDF.'
            )}
          </DialogDescription>
          <div className="flex items-center gap-3 text-slate-100 text-sm flex-wrap">
            <span className="font-semibold">{detail?.fileName}</span>
            <Badge variant="secondary" className="bg-slate-200/20 text-slate-100 border-slate-100/30">
              PDF
            </Badge>
            <span>{formatFileSize(detail?.fileSizeBytes ?? 0)}</span>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-slate-50">
          {detail?.previewHtml ? (
            <iframe
              title={detail.fileName}
              srcDoc={detail.previewHtml}
              className="w-full h-full border-0 bg-white"
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            />
          ) : null}
        </div>

        <DialogFooter className="px-4 sm:px-6 py-4 border-t bg-white shrink-0 gap-2">
          <Button variant="outline" type="button" onClick={closeDrawer}>
            {t('common.close')}
          </Button>
          <Button type="button" onClick={() => { void handleShare() }} disabled={sharing}>
            {sharing ? t('common.loading') : t('common.share')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
